import { HTTP_CLIENT_ERRORS } from '@/lib/constants/errors'
import log from '@/providers/log'
import type { IServiceError } from '@/types/error'

interface IErrorResponse {
  error?: string | { message?: string; [key: string]: unknown }
  message?: string
  details?: unknown[]
  errors?: unknown[]
}

const LOGGED_BODY_LENGTH = 500
const FIRST_SERVER_ERROR_STATUS = 500

const STATUS_MESSAGES: Record<number, string> = {
  401: HTTP_CLIENT_ERRORS.SESSION_EXPIRED,
  403: HTTP_CLIENT_ERRORS.FORBIDDEN,
  404: HTTP_CLIENT_ERRORS.NOT_FOUND,
}

export function getStatusMessage(status: number): string {
  if (status >= FIRST_SERVER_ERROR_STATUS) return HTTP_CLIENT_ERRORS.SERVICE_UNAVAILABLE
  return STATUS_MESSAGES[status] ?? HTTP_CLIENT_ERRORS.REQUEST_FAILED
}

export function buildErrorResult(message: string, details?: unknown): IServiceError {
  return { error: { message, details } } as IServiceError
}

export function stripQuery(url: string): string {
  return url.split('?')[0]
}

export function getErrorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function toLogSnippet(text: string): string {
  return text.replace(/\s+/g, ' ').trim().slice(0, LOGGED_BODY_LENGTH)
}

function getErrorCauseText(error: unknown): string {
  const cause = error instanceof Error ? error.cause : undefined
  return cause == null ? '' : ` (cause: ${getErrorText(cause)})`
}

// Opt-in (shouldLogErrorDetails): the full upstream picture, since the returned message is usually generic.
export function logNetworkErrorDetails(method: string, url: string, error: unknown): void {
  log.error(`${method} ${url} failed: ${getErrorText(error)}${getErrorCauseText(error)}`)
}

export async function logErrorResponseDetails(method: string, response: Response): Promise<void> {
  const body = await response.text().catch(() => '')
  log.error(
    `${method} ${response.url} returned HTTP ${response.status} ${response.statusText}: ${body.replace(/\s+/g, ' ').trim()}`,
  )
}

function logUnreadableErrorBody(response: Response, text: string): void {
  const message = `HTTP ${response.status} from ${stripQuery(response.url)}: ${toLogSnippet(text)}`
  if (response.status >= FIRST_SERVER_ERROR_STATUS) log.warn(message)
  else log.debug(message)
}

function parseErrorBody(response: Response, text: string): IErrorResponse | undefined {
  const contentType = response.headers.get('content-type') ?? ''
  // A non-JSON body is a proxy or framework page (HTML 502, "404 page not found"), not a message
  if (!contentType.includes('application/json')) return undefined
  try {
    return JSON.parse(text) as IErrorResponse
  } catch {
    return undefined
  }
}

function getPreservedMessage(errorData: IErrorResponse, statusMessage: string): string {
  const { error } = errorData
  if (typeof error === 'string') return error
  return error?.message ?? errorData.message ?? statusMessage
}

function getMessageFromErrorPayload(error: IErrorResponse['error'], statusMessage: string): string {
  if (error == null) return statusMessage
  return typeof error === 'string' ? error : (error.message ?? statusMessage)
}

export async function handleErrorResponse(response: Response, preserveFullError = false): Promise<IServiceError> {
  const statusMessage = getStatusMessage(response.status)
  const text = await response.text().catch(() => '')
  const errorData = parseErrorBody(response, text)
  if (!errorData) {
    logUnreadableErrorBody(response, text)
    return buildErrorResult(statusMessage)
  }
  // Needed in some callers (like Fleet) to propagate proper error
  if (preserveFullError) return buildErrorResult(getPreservedMessage(errorData, statusMessage), errorData)
  const rawError = errorData.error
  const message = getMessageFromErrorPayload(rawError, statusMessage)
  if (rawError != null && typeof rawError === 'object') {
    return buildErrorResult(message, rawError)
  }
  return buildErrorResult(message)
}
